import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  TextField,
  Button, 
  Avatar, 
  Grid, 
  Divider,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { AccountCircle as AccountCircleIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api';

interface ProfileData {
  name: string;
  email: string;
  apiKey: string;
  agentApiKey: string | null;
  dateJoined: string;
  lastLogin: string;
  usageStats: {
    totalWorkflows: number;
    totalExecutions: number;
    apiCalls: number;
  };
}

const Profile: React.FC = () => {
  const { user, regenerateAgentApiKey, deleteAgentApiKey } = useAuth();
  const [profile, setProfile] = useState<ProfileData>({
    name: '',
    email: '',
    apiKey: '',
    agentApiKey: null,
    dateJoined: '',
    lastLogin: '',
    usageStats: {
      totalWorkflows: 0,
      totalExecutions: 0,
      apiCalls: 0
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [error, setError] = useState<string | null>(null);
  
  // Password change state
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userData = await api.auth.getProfile();
        
        // Transform user data to match ProfileData structure
        const profileData: ProfileData = {
          name: userData.name || '',
          email: userData.email || '',
          apiKey: userData.apiKey || '',
          agentApiKey: userData.agentApiKey || null,
          dateJoined: userData.createdAt ? String(userData.createdAt) : '',
          lastLogin: userData.lastLoginAt ? String(userData.lastLoginAt) : '',
          usageStats: {
            totalWorkflows: 0, // These would need to be fetched from a different endpoint
            totalExecutions: 0,
            apiCalls: 0
          }
        };
        
        setProfile(profileData);
        setEditedName(profileData.name);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch profile');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, []);

  const handleEditToggle = async () => {
    if (isEditing) {
      try {
        const updatedUser = await api.auth.updateProfile({ name: editedName });
        
        // Update the profile with the response
        setProfile({
          ...profile,
          name: updatedUser.name
        });
        
        setSnackbar({
          open: true,
          message: 'Profile updated successfully',
          severity: 'success'
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err instanceof Error ? err.message : 'Failed to update profile',
          severity: 'error'
        });
      }
    }
    setIsEditing(!isEditing);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRegenerateAgentApiKey = async () => {
    try {
      setLoading(true);
      const agentApiKey = await regenerateAgentApiKey();
      setProfile({
        ...profile,
        agentApiKey: agentApiKey
      });
      setSnackbar({
        open: true,
        message: 'Agent API key generated successfully',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to generate agent API key',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAgentApiKey = async () => {
    try {
      setLoading(true);
      await deleteAgentApiKey();
      setProfile({
        ...profile,
        agentApiKey: null
      });
      setSnackbar({
        open: true,
        message: 'Agent API key deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to delete agent API key',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    // Validate passwords
    if (newPassword !== confirmPassword) {
      setSnackbar({
        open: true,
        message: 'New passwords do not match',
        severity: 'error'
      });
      return;
    }

    if (newPassword.length < 6) {
      setSnackbar({
        open: true,
        message: 'New password must be at least 6 characters long',
        severity: 'error'
      });
      return;
    }

    try {
      setPasswordChanging(true);
      await api.auth.changePassword(currentPassword, newPassword);
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      setSnackbar({
        open: true,
        message: 'Password changed successfully',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to change password',
        severity: 'error'
      });
    } finally {
      setPasswordChanging(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ width: 64, height: 64 }}>
                <AccountCircleIcon sx={{ fontSize: 64 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" gutterBottom>
                  {isEditing ? (
                    <TextField
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      size="small"
                    />
                  ) : (
                    profile.name
                  )}
                </Typography>
                <Typography color="textSecondary">
                  {profile.email}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Account Details
              </Typography>
              <Typography variant="body2" color="textSecondary">
                API Key: {profile.apiKey}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Date Joined: {profile.dateJoined ? new Date(Number(profile.dateJoined)).toLocaleDateString() : 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Last Login: {profile.lastLogin ? new Date(Number(profile.lastLogin)).toLocaleDateString() : 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Agent API Key
              </Typography>
              {profile.agentApiKey ? (
                <>
                  <Typography variant="body2" gutterBottom sx={{ overflowWrap: 'break-word' }}>
                    Your Agent API Key: {profile.agentApiKey}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Use this API key to authenticate API calls to your agents. Keep it secret and secure.
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      color="primary" 
                      onClick={handleRegenerateAgentApiKey}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Regenerate Key'}
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      color="error" 
                      onClick={handleDeleteAgentApiKey}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Delete Key'}
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    You don't have an Agent API key yet. Generate one to make API calls to your agents.
                  </Typography>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleRegenerateAgentApiKey}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Generate Agent API Key'}
                  </Button>
                </>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Change Password
              </Typography>
              <Box sx={{ maxWidth: 400 }}>
                <TextField
                  fullWidth
                  type="password"
                  label="Current Password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  margin="normal"
                  size="small"
                />
                <TextField
                  fullWidth
                  type="password"
                  label="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  margin="normal"
                  size="small"
                  helperText="Password must be at least 6 characters long"
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  size="small"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePasswordChange}
                  disabled={passwordChanging || !currentPassword || !newPassword || !confirmPassword}
                  sx={{ mt: 2 }}
                >
                  {passwordChanging ? <CircularProgress size={24} /> : 'Change Password'}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Usage Statistics
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Workflows: {profile.usageStats.totalWorkflows}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Executions: {profile.usageStats.totalExecutions}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                API Calls: {profile.usageStats.apiCalls}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleEditToggle}
                >
                  {isEditing ? 'Save Changes' : 'Edit Profile'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile; 